// Please keep files in back/src/api/compute-field updated

import { SupportedLocale } from "@/i18n";
import { ComponentProps, ReactNode } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  ArtworkDescriptionEnsembleFixeCF,
  ArtworkDescriptionEnsembleTemporaireCf,
  ArtworkDimensionsCadreCF,
  ArtworkDimensionsCF,
  ArtworkDimensionsHorsMargesCF,
  ArtworkLocalisationHistoriqueCF,
  ArtworkMainTitleCF,
  ArtworkRelatedAuthorsCF,
  ArtworkSignatureCF,
  ArtworkSummaryCF,
  ArtworkSupportTechniqueCF,
  UnstyledArtworkTitleCF,
} from "./compute-field/artwork";
import { RelatedAuctionsCF } from "./compute-field/artwork-in-auction";
import { RelatedBibliographiesCF } from "./compute-field/artwork-in-bibliography";
import { RelatedExhibitionsCF } from "./compute-field/artwork-in-exhibition";
import { AuctionSummaryCF } from "./compute-field/auction";
import { AudiovisualSummaryCF } from "./compute-field/audiovisual";
import {
  BibliographieDimensionsCF,
  BibliographieLongSummaryCF,
} from "./compute-field/bibliography";
import { CollectorNameCF } from "./compute-field/collector";
import { DocumentSummaryCF } from "./compute-field/document";
import { ExhibitionSummaryCF } from "./compute-field/exposition";
import {
  MultipleprintDimensionImageCF,
  MultipleprintDimensionsCF,
  MultipleprintRelatedAuthorsCF,
  MultipleprintSignatureCF,
  MultipleprintSummaryCF,
  MultipleprintSupportTechniqueCF,
  MultipleprintTitleCF,
} from "./compute-field/multipleprint";
import {
  PhotographyDimensionsCF,
  PhotographySummaryCF,
} from "./compute-field/photographie";
import { EcritSummaryCF } from "./compute-field/writing";
import { DateCF } from "./compute-field/shared/title";

export type ComputeFieldComponent<T, CustomProps = unknown> = (
  props: {
    input: T;
    lineBreak?: boolean;
    endWithPeriod?: boolean;
    language: SupportedLocale;
    translation?: (key: string) => string;
  } & CustomProps,
) => ReactNode;

function computeFieldRenderer<T>(
  CFComponent: ComputeFieldComponent<T>,
): (
  input: T,
  params: Omit<ComponentProps<ComputeFieldComponent<T>>, "input">,
) => string {
  return (input: T, params) =>
    renderToStaticMarkup(CFComponent({ input, ...params }));
}

export function unwrapPTag(input: string) {
  return input.replace(/<p[^>]*>(.*?)<\/p>/g, "$1");
}

////////// Artwork
export const computeFieldUnstyledArtwork = computeFieldRenderer(
  UnstyledArtworkTitleCF,
);

export const computeFieldDimensionsArtwork =
  computeFieldRenderer(ArtworkDimensionsCF);

export const computeFieldTitleArtwork =
  computeFieldRenderer(ArtworkMainTitleCF);

export const computeFieldDimensionsHorsMargesArtwork = computeFieldRenderer(
  ArtworkDimensionsHorsMargesCF,
);

export const computeFieldDimensionsCadreArtwork = computeFieldRenderer(
  ArtworkDimensionsCadreCF,
);

export const computeFieldRelatedAuthorsArtwork = computeFieldRenderer(
  ArtworkRelatedAuthorsCF,
);

export const computeFieldSupportTechniqueArtwork = computeFieldRenderer(
  ArtworkSupportTechniqueCF,
);

export const computeFieldSignatureArtwork =
  computeFieldRenderer(ArtworkSignatureCF);

export const computeFieldSummaryArtwork =
  computeFieldRenderer(ArtworkSummaryCF);

export const computeFieldDescriptionEnsembleFixeArtwork = computeFieldRenderer(
  ArtworkDescriptionEnsembleFixeCF,
);

export const computeFieldDescriptionEnsembleTemporaireArtwork =
  computeFieldRenderer(ArtworkDescriptionEnsembleTemporaireCf);

export const computeFieldLocalisationHistoriqueArtwork = computeFieldRenderer(
  ArtworkLocalisationHistoriqueCF,
);

export const computeFieldRelatedExhibitions =
  computeFieldRenderer(RelatedExhibitionsCF);

export const computeFieldRelatedAuctions =
  computeFieldRenderer(RelatedAuctionsCF);

export const computeFieldRelatedBibliographies = computeFieldRenderer(
  RelatedBibliographiesCF,
);

///////// Bibliography

export const computeFieldDimensionsBibliographie = computeFieldRenderer(
  BibliographieDimensionsCF,
);

export const computeFieldLongSummaryBibliographie = computeFieldRenderer(
  BibliographieLongSummaryCF,
);

///////// Collector

export const computeFieldCollectorName = computeFieldRenderer(CollectorNameCF);

///////// Auction

export const computeFieldAuctionSummary =
  computeFieldRenderer(AuctionSummaryCF);

///////// Multipleprint

export const computeFieldMultipleprintDimensions = computeFieldRenderer(
  MultipleprintDimensionsCF,
);

export const computeFieldMultipleprintDimensionImage = computeFieldRenderer(
  MultipleprintDimensionImageCF,
);

export const computeFieldMultipleprintRelatedAuthors = computeFieldRenderer(
  MultipleprintRelatedAuthorsCF,
);

export const computeFieldMultipleprintSupportTechnique = computeFieldRenderer(
  MultipleprintSupportTechniqueCF,
);

export const computeFieldMultipleprintSignature = computeFieldRenderer(
  MultipleprintSignatureCF,
);

export const computeFieldMultipleprintTitle =
  computeFieldRenderer(MultipleprintTitleCF);

export const computeFieldMultipleprintSummary = computeFieldRenderer(
  MultipleprintSummaryCF,
);

///////// Writing

export const computeFieldWritingDescription =
  computeFieldRenderer(EcritSummaryCF);

///////// Audiovisual

export const computeFieldAudiovisualSummary =
  computeFieldRenderer(AudiovisualSummaryCF);

///////// Document

export const computeFieldDocumentSummary =
  computeFieldRenderer(DocumentSummaryCF);

///////// Exhibition

export const computeFieldExhibitionSummary =
  computeFieldRenderer(ExhibitionSummaryCF);

///////// Photography

export const computeFieldPhotographySummary =
  computeFieldRenderer(PhotographySummaryCF);

export const computeFieldPhotographyDimensions = computeFieldRenderer(
  PhotographyDimensionsCF,
);

export const computeFieldDate = computeFieldRenderer(DateCF);
